import { useState, lazy, Suspense, useEffect } from "react";

// Lazy load route components for better code splitting
const ChatConvo = lazy(() =>
  import("@/features/chat/components/ChatConvo").then((m) => ({
    default: m.ChatConvo,
  })),
);
const FilesPage = lazy(() =>
  import("@/features/files/components/FilesPage").then((m) => ({
    default: m.FilesPage,
  })),
);
const GebrekenPage = lazy(() =>
  import("@/features/gebreken").then((m) => ({
    default: m.GebrekenPage,
  })),
);
import { useArticlePreloader } from "@/features/gebreken";
import { useAuth } from "@/features/auth";
import { UserProvider } from "@/providers/User";
import { StreamProvider } from "@/providers/Stream";
import { ThreadProvider } from "@/providers/Conversation";
import { ArtifactProvider } from "@/features/artifacts";
import { StorageProvider } from "@/contexts/StorageContext";
import { AuthProvider, AuthGate } from "@/features/auth";
import { ConversationSelectionGate } from "@/features/conversations";
import { CitationExpansionProvider } from "@/features/chat/contexts/CitationExpansionContext";
import { Toaster } from "@/components/ui/sonner";
import { TopHeader } from "@/components/TopHeader";
import { Header, type AppMode } from "@/components/Header";
import { useHealthCheck } from "@/hooks/useHealthCheck";
import { FEATURES } from "@/config";
import {
  OPEN_PAGE_KEY,
  IDLE_CALLBACK_TIMEOUT,
  PRELOAD_DELAY,
} from "@/constants";

const AppInner: React.FC = () => {
  const [mode, setMode] = useState<AppMode>(() => {
    // Initialize from sessionStorage, default to 'chat' if not set or invalid
    const savedMode = sessionStorage.getItem(OPEN_PAGE_KEY);

    // If gebreken is disabled and savedMode is "gebreken", fallback to "chat"
    if (savedMode === "gebreken" && !FEATURES.gebrekenPageEnabled) {
      return "chat";
    }

    // Valid modes: chat, files, gebreken
    if (savedMode === "chat" || savedMode === "files" || savedMode === "gebreken") {
      return savedMode;
    }

    return "chat";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { preload: preloadArticles } = useArticlePreloader();
  const { firebaseUser, isAuthLoading } = useAuth();

  // Trigger health check on mount to wake up backend (cold start)
  useHealthCheck();

  // Preload articles data in background when component mounts
  // Skip preloading if gebreken page is disabled (performance optimization)
  // Wait for Firebase Auth to be ready since Storage requires authentication
  useEffect(() => {
    if (!FEATURES.gebrekenPageEnabled) {
      return; // Skip article preloading when gebreken is disabled
    }

    // Wait for auth to complete before preloading (Storage requires auth)
    if (isAuthLoading || !firebaseUser) {
      return;
    }

    const preloadArticlesIdle = () => {
      preloadArticles().catch((error) => {
        console.error("Failed to preload articles:", error);
      });
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadArticlesIdle, {
        timeout: IDLE_CALLBACK_TIMEOUT,
      });
      return () => window.cancelIdleCallback(idleId);
    } else {
      const timeoutId = setTimeout(preloadArticlesIdle, PRELOAD_DELAY);
      return () => clearTimeout(timeoutId);
    }
  }, [preloadArticles, isAuthLoading, firebaseUser]);

  return (
    <div className="h-screen-safe bg-background flex w-full flex-col overflow-hidden">
      {/* Top header with Utrecht logo and Help */}
      <TopHeader />

      {/* Content with contexts */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <Toaster />
        <ThreadProvider>
          <StreamProvider>
            <StorageProvider>
              {/* Navigation header for chat/files/gebreken mode */}
              <Header
                mode={mode}
                onModeChange={setMode}
                sidebarOpen={sidebarOpen}
                onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
              />
              <ConversationSelectionGate>
                {mode === "files" ? (
                  <Suspense fallback={null}>
                    <FilesPage
                      sidebarOpen={sidebarOpen}
                      onSidebarClose={() => setSidebarOpen(false)}
                    />
                  </Suspense>
                ) : mode === "gebreken" && FEATURES.gebrekenPageEnabled ? (
                  <Suspense fallback={null}>
                    <GebrekenPage
                      sidebarOpen={sidebarOpen}
                      onSidebarClose={() => setSidebarOpen(false)}
                    />
                  </Suspense>
                ) : (
                  <CitationExpansionProvider>
                    <Suspense fallback={null}>
                      <ChatConvo
                        sidebarOpen={sidebarOpen}
                        onSidebarClose={() => setSidebarOpen(false)}
                      />
                    </Suspense>
                  </CitationExpansionProvider>
                )}
              </ConversationSelectionGate>
            </StorageProvider>
          </StreamProvider>
        </ThreadProvider>
      </div>
    </div>
  );
};

// Wrapper component that provides all context providers
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AuthGate>
        <UserProvider>
          <ArtifactProvider>
            <AppInner />
          </ArtifactProvider>
        </UserProvider>
      </AuthGate>
    </AuthProvider>
  );
};

export default App;
